/**
 * CommentSidebar/index.tsx — 8210-C
 *
 * Sidebar panel that sits to the right of the editor iframe.
 * Cards are stacked in a scrollable list. Clicking a card scrolls it
 * to the center of the sidebar and the annotation to the center of the editor.
 *
 * Layout:
 *   SidebarContainer (full height, stretches with iframe)
 *     └─ SidebarInner (CSS sticky, viewport-height, flex column)
 *          ├─ SidebarHeader (flex-shrink: 0 — never scrolls)
 *          └─ Virtuoso ScrollableCardArea (flex: 1, overflow-y: auto)
 *               └─ CardListContainer (virtualized stacked cards + optional NewComment slot)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Virtuoso, type VirtuosoHandle } from "react-virtuoso";
import { FeedbackCard } from "../../FeedbackCard";
import { UserCommentCard } from "../../FeedbackCard/UserCommentCard";
import { useCommentStore } from "../../../stores/commentStore";
import { useProjectStore } from "../../../stores/projectStore";
import { useStoryBibleStore } from "../../../stores/storyBibleStore";
import useEditPluginInstallations from "../../../hooks/useEditPluginInstallations";
import { CommentSidebarResizeHandle } from "./CommentSidebarResizeHandle";
import SidebarHeader from "./SidebarHeader";
import {
  SidebarContainer,
  SidebarInner,
  ScrollableCardArea,
  CardListContainer,
  EmptyStateContainer,
  EmptyStateMessage,
  PaginationLoadingContainer,
  PaginationSpinner,
} from "./styles";
import { COMMENT_FILTER_TYPES } from "./commentStatus";
import SVG from "react-inlinesvg";

function getSidebarEmptyState(filter: string): { message: string; showComposerHintIcons: boolean } {
  if (filter === COMMENT_FILTER_TYPES.RESOLVED) {
    return { message: "No resolved comments.", showComposerHintIcons: false };
  }
  if (filter === COMMENT_FILTER_TYPES.IGNORED) {
    return { message: "No ignored comments.", showComposerHintIcons: false };
  }
  return {
    message: "Select text to leave a comment, or use Feedback to get suggestions.",
    showComposerHintIcons: true,
  };
}

// -----------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------

const VirtuosoScroller = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<"div">>(
  (props, ref) => (
    <ScrollableCardArea
      {...props}
      ref={ref}
    />
  )
);
VirtuosoScroller.displayName = "CommentSidebarVirtuosoScroller";

const VirtuosoList = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<"div">>(
  ({ style, ...props }, ref) => (
    <CardListContainer
      {...props}
      ref={ref}
      style={{
        ...style,
        boxSizing: "border-box",
        paddingLeft: 0,
        paddingRight: 20,
      }}
    />
  )
);
VirtuosoList.displayName = "CommentSidebarVirtuosoList";

function VirtuosoFooter(): JSX.Element {
  return <div style={{ height: 50 }} />;
}

function VirtuosoItem({ children, ...props }: React.ComponentPropsWithoutRef<"div">): JSX.Element {
  return (
    <div
      {...props}
      style={{
        ...props.style,
        boxSizing: "border-box",
        paddingBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

const virtuosoComponents = {
  Scroller: VirtuosoScroller,
  List: VirtuosoList,
  Footer: VirtuosoFooter,
  Item: VirtuosoItem,
};

interface Comment {
  id: string;
  annotationId: string;
  aiId: string | null;
  content: string;
  original?: string | null;
  suggestion?: string | null;
  metadata?: Record<string, unknown>;
  /** ISO or display string; required by card components for typing / future UI */
  createdAt?: string;
  replies?: unknown[];
  displayName?: string;
  userId?: string;
  [key: string]: unknown;
}

/** Narrowed shape both FeedbackCard and UserCommentCard accept without undefined holes. */
type CardComment = Comment & {
  original: string | null;
  suggestion: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  replies: unknown[];
};

type CommentListItem =
  | {
      type: "composer";
      id: "new-comment-composer";
    }
  | {
      type: "comment";
      id: string;
      comment: Comment;
    }
  | {
      type: "loader";
      id: "comments-pagination-loader";
    };

/**
 * Sidebar comments can be partial (e.g. store/API drift). FeedbackCard and UserCommentCard
 * expect stable shapes: non-optional `original`/`suggestion`, `replies`, `createdAt`, object `metadata`.
 */
function normalizeCommentForCards(comment: Comment): CardComment {
  const metadata =
    comment.metadata &&
    typeof comment.metadata === "object" &&
    !Array.isArray(comment.metadata)
      ? (comment.metadata as Record<string, unknown>)
      : {};
  const normalized: CardComment = {
    ...comment,
    aiId: comment.aiId ?? null,
    content: typeof comment.content === "string" ? comment.content : "",
    original: comment.original ?? null,
    suggestion: comment.suggestion ?? null,
    metadata,
    createdAt: typeof comment.createdAt === "string" ? comment.createdAt : "",
    replies: Array.isArray(comment.replies) ? comment.replies : [],
  };
  return normalized;
}

interface CommentSidebarProps {
  comments: Comment[];
  onNavigateUp: () => void;
  onNavigateDown: () => void;
  onDeleteAll: (filterType: string) => void;
  expandedCardId: string | null;
  onExpandCard: (id: string | null) => void;
  /** Called when clicking an already-expanded card to scroll its annotation into view */
  onScrollToAnnotation?: (commentId: string) => void;
  onApply: (comment: Comment) => void;
  onDismiss: (comment: Comment) => void;
  onDeleteComment: (commentId: string, annotationId: string) => void;
  onUndoComment?: (comment: Comment) => void;
  canUndoComment?: (comment: Comment) => boolean;
  canNavigateUp: boolean;
  canNavigateDown: boolean;
  /** Height of the Draft panel above the editor, used to offset sticky top */
  draftPanelHeight?: number;
  /** Slot for NewComment box (rendered inline at document position) */
  children?: React.ReactNode;
  /**
   * When this matches `expandedCardId` on a newly expanded card, play the wiggle animation
   * (e.g. user opened the thread from an agentic chat `sudowrite://comment/...` link).
   */
  wiggleTargetCommentId?: string | null;
  /** Index in the sorted comment list where children should be inserted */
  newCommentInsertIndex?: number;
  /** Document ID needed for submitting replies */
  documentId?: string | null;
  /** Location label for analytics (e.g. "Editor") */
  commentLocation?: string;
  /** Whether the app is in mobile mode */
  isMobile?: boolean;
  selectedFilter?: string;
  onFilterChange?: (filterType: string) => void;
  /** Totals across all statuses (caller must derive from full comment list; not filtered `comments`). */
  commentCounts: Record<string, number>;
  commentsLoaded?: boolean;
  isLoadingComments?: boolean;
  hasMoreComments?: boolean;
  isLoadingMoreComments?: boolean;
  onLoadMoreComments?: () => void;
}

// -----------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------

export function CommentSidebar({
  comments,
  onNavigateUp,
  onNavigateDown,
  onDeleteAll,
  expandedCardId,
  onExpandCard,
  onScrollToAnnotation,
  onApply,
  onDismiss,
  onDeleteComment,
  onUndoComment,
  canUndoComment,
  canNavigateUp,
  canNavigateDown,
  draftPanelHeight = 0,
  children,
  wiggleTargetCommentId = null,
  newCommentInsertIndex = 0,
  documentId,
  commentLocation,
  isMobile,
  selectedFilter = COMMENT_FILTER_TYPES.OPEN,
  onFilterChange,
  commentCounts,
  commentsLoaded = true,
  isLoadingComments = false,
  hasMoreComments = false,
  isLoadingMoreComments = false,
  onLoadMoreComments,
}: CommentSidebarProps): JSX.Element | null {
  const storedSidebarWidth = useProjectStore((state) => state.commentSidebarWidth);
  const previewSidebarWidth = useProjectStore((state) => state.commentSidebarWidthPreview);
  const setCommentSidebarWidth = useProjectStore((state) => state.setCommentSidebarWidth);
  const setCommentSidebarWidthPreview = useProjectStore((state) => state.setCommentSidebarWidthPreview);
  const sidebarWidth = previewSidebarWidth ?? storedSidebarWidth;
  const draftStickyBarHeight = useStoryBibleStore((state: any) => state.draftStickyBarHeight);
  const { installations } = useEditPluginInstallations();
  const showDeleteOnlyAction =
    selectedFilter === COMMENT_FILTER_TYPES.RESOLVED ||
    selectedFilter === COMMENT_FILTER_TYPES.IGNORED;
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  /** Ref to the ScrollableCardArea (the scrollable viewport) */
  const scrollRef = useRef<HTMLDivElement>(null);
  /** Ref to the inline new-comment composer slot when present */
  const newCommentSlotRef = useRef<HTMLDivElement | null>(null);
  /** Track which comment ID should wiggle (only when `wiggleTargetCommentId` matches expansion) */
  const [wiggleId, setWiggleId] = useState<string | null>(null);
  /** Previous expandedCardId to detect changes */
  const prevExpandedIdRef = useRef<string | null>(null);
  const previousCommentCountRef = useRef(comments.length);
  const lastLoadMoreScrollTopRef = useRef<number | null>(null);
  const lastComposerScrollTargetRef = useRef<{ index: number; commentCount: number } | null>(null);
  /** Timeout refs for wiggle animation cleanup */
  const wiggleStartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wiggleClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasInlineNewCommentComposer = React.Children.toArray(children).length > 0;
  const composerIndex = useMemo(() => {
    return Math.min(Math.max(newCommentInsertIndex, 0), comments.length);
  }, [comments.length, newCommentInsertIndex]);
  const listItems = useMemo<CommentListItem[]>(() => {
    const items: CommentListItem[] = [];
    comments.forEach((comment, index) => {
      if (!comment?.id) return;
      if (hasInlineNewCommentComposer && index === composerIndex) {
        items.push({ type: "composer", id: "new-comment-composer" });
      }
      items.push({ type: "comment", id: comment.id, comment });
    });
    if (hasInlineNewCommentComposer && composerIndex >= comments.length) {
      items.push({ type: "composer", id: "new-comment-composer" });
    }
    if (hasMoreComments || isLoadingMoreComments) {
      items.push({ type: "loader", id: "comments-pagination-loader" });
    }
    return items;
  }, [comments, composerIndex, hasInlineNewCommentComposer, hasMoreComments, isLoadingMoreComments]);
  const commentIndexById = useMemo(() => {
    const indexById = new Map<string, number>();
    listItems.forEach((item, index) => {
      if (item.type === "comment") {
        indexById.set(item.id, index);
      }
    });
    return indexById;
  }, [listItems]);
  const computeItemKey = useCallback((index: number, item: CommentListItem | undefined) => {
    return item?.id ?? `comment-sidebar-item-${index}`;
  }, []);
  const scrollListIndexTo = useCallback((
    index: number,
    align: "start" | "center" | "end" = "start",
    behavior: ScrollBehavior = "auto",
  ) => {
    virtuosoRef.current?.scrollToIndex({
      index,
      align,
      behavior,
    });
  }, []);
  const scrollListIndexToCenter = useCallback((index: number, behavior: ScrollBehavior = "smooth") => {
    scrollListIndexTo(index, "center", behavior);
  }, [scrollListIndexTo]);
  const scrollListIndexToHeader = useCallback((index: number, behavior: ScrollBehavior = "auto") => {
    scrollListIndexTo(index, "start", behavior);
  }, [scrollListIndexTo]);
  const handleEndReached = useCallback(() => {
    if (!hasMoreComments || isLoadingMoreComments) return;
    const scrollEl = scrollRef.current;
    if (scrollEl) {
      const currentScrollTop = scrollEl.scrollTop;
      if (
        lastLoadMoreScrollTopRef.current !== null &&
        currentScrollTop <= lastLoadMoreScrollTopRef.current + 24
      ) {
        return;
      }
      lastLoadMoreScrollTopRef.current = currentScrollTop;
    }
    onLoadMoreComments?.();
  }, [hasMoreComments, isLoadingMoreComments, onLoadMoreComments]);

  useEffect(() => {
    if (!hasMoreComments || comments.length < previousCommentCountRef.current) {
      lastLoadMoreScrollTopRef.current = null;
    }
    previousCommentCountRef.current = comments.length;
  }, [comments.length, hasMoreComments]);

  // Clear expanded card only after the active list is fully loaded.
  // This prevents the wiggle animation from triggering on a different card
  // when the currently expanded comment is deleted (e.g., batch delete).
  // Also suppresses scroll-driven sync from immediately selecting another card.
  useEffect(() => {
    if (!expandedCardId) return;
    const stillExists = comments.some((c) => c.id === expandedCardId);
    if (stillExists) return;

    const listCanStillLoadExpandedCard =
      !commentsLoaded || isLoadingComments || isLoadingMoreComments || hasMoreComments;
    if (listCanStillLoadExpandedCard) return;

    // Clear the expanded card without triggering a new selection.
    onExpandCard(null);
    // Suppress auto-selection of another card via scroll-driven sync after the expanded
    // comment disappears (e.g. batch delete). EditorContainer reads this from commentStore.
    useCommentStore.getState().setCommentScrollSyncSuppressedUntil(Date.now() + 2000);
  }, [
    comments,
    commentsLoaded,
    expandedCardId,
    hasMoreComments,
    isLoadingComments,
    isLoadingMoreComments,
    onExpandCard,
  ]);

  // When expandedCardId changes, scroll that card header into view in the sidebar list.
  // Chat-linked expansion: instant scroll first, then wiggle — avoids transform + smooth
  // vertical scroll on the same node (reads glitchy). Other expansion paths also use
  // instant header alignment so tall expanded cards do not bounce or center on body text.
  // Use Virtuoso's item-index scroll so virtual row offsets stay authoritative and
  // parent containers (like .editor-stack) are not scrolled by scrollIntoView.
  useEffect(() => {
    if (!expandedCardId) {
      prevExpandedIdRef.current = null;
      return;
    }

    const itemIndex = commentIndexById.get(expandedCardId);
    if (itemIndex === undefined) return;
    if (expandedCardId === prevExpandedIdRef.current) return;

    // If this is a new expansion (different from previous), apply sidebar scroll / wiggle rules above.
    // Comment list refreshes can rebuild `commentIndexById`; those should not yank the user's sidebar scroll.
    if (wiggleStartTimeoutRef.current) {
      clearTimeout(wiggleStartTimeoutRef.current);
      wiggleStartTimeoutRef.current = null;
    }
    if (wiggleClearTimeoutRef.current) {
      clearTimeout(wiggleClearTimeoutRef.current);
      wiggleClearTimeoutRef.current = null;
    }
    // Immediately clear any previous wiggle to prevent multiple comments animating
    setWiggleId(null);

    const shouldWiggle =
      typeof wiggleTargetCommentId === "string" &&
      wiggleTargetCommentId.length > 0 &&
      wiggleTargetCommentId === expandedCardId;

    if (shouldWiggle) {
      // Two rAFs: card layout (expanded height) is often ready after paint.
      scrollListIndexToHeader(itemIndex);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Defer wiggle one task so the list scroll has applied; wiggle uses transform and
          // must not overlap smooth vertical motion of the list or the main editor.
          wiggleStartTimeoutRef.current = setTimeout(() => {
            setWiggleId(expandedCardId);
            wiggleStartTimeoutRef.current = null;
            wiggleClearTimeoutRef.current = setTimeout(() => {
              setWiggleId(null);
              wiggleClearTimeoutRef.current = null;
            }, 900);
          }, 0);
        });
      });
    } else {
      // New expansion from sidebar / editor / scroll — no wiggle; scroll list immediately.
      scrollListIndexToHeader(itemIndex);
    }
    prevExpandedIdRef.current = expandedCardId;

    // Cleanup timeouts when component unmounts or expandedCardId changes
    return () => {
      if (wiggleStartTimeoutRef.current) {
        clearTimeout(wiggleStartTimeoutRef.current);
      }
      if (wiggleClearTimeoutRef.current) {
        clearTimeout(wiggleClearTimeoutRef.current);
      }
    };
  }, [commentIndexById, expandedCardId, scrollListIndexToHeader, wiggleTargetCommentId]);

  // Keep the inline NewComment composer visible when it opens in the list.
  useEffect(() => {
    if (!hasInlineNewCommentComposer) {
      lastComposerScrollTargetRef.current = null;
      return;
    }
    const previousTarget = lastComposerScrollTargetRef.current;
    const commentsWereAppended =
      previousTarget !== null && comments.length > previousTarget.commentCount;
    const composerOpened = previousTarget === null;
    const composerMovedWithinLoadedList =
      previousTarget !== null &&
      composerIndex !== previousTarget.index &&
      !commentsWereAppended;
    lastComposerScrollTargetRef.current = {
      index: composerIndex,
      commentCount: comments.length,
    };
    if (!composerOpened && !composerMovedWithinLoadedList) return;

    requestAnimationFrame(() => {
      scrollListIndexToCenter(composerIndex);
    });
  }, [comments.length, composerIndex, hasInlineNewCommentComposer, scrollListIndexToCenter]);

  // Compute dynamic offsets for the sidebar when the Draft panel is open:
  // - marginTop: reduce the negative pull-up when Draft takes space above the editor
  // - top: push sidebar below Draft's GeneratePanel when it becomes fixed on scroll
  const sidebarStyle = useMemo(() => {
    const style: React.CSSProperties = {};
    if (draftPanelHeight > 0) {
      style.marginTop = `-${Math.max(0, 200 - draftPanelHeight)}px`;
    }
    if (draftStickyBarHeight > 0) {
      style.top = `${48 + draftStickyBarHeight + 20}px`;
    }
    return Object.keys(style).length > 0 ? style : undefined;
  }, [draftPanelHeight, draftStickyBarHeight]);

  const emptyState = useMemo(() => getSidebarEmptyState(selectedFilter), [selectedFilter]);
  const renderCommentItem = useCallback(
    (_index: number, item: CommentListItem | undefined) => {
      if (!item) return null;

      if (item.type === "composer") {
        return <div ref={newCommentSlotRef}>{children}</div>;
      }

      if (item.type === "loader") {
        return (
          <PaginationLoadingContainer
            role="status"
            aria-live="polite"
          >
            <PaginationSpinner aria-hidden="true" />
            <span>{isLoadingMoreComments ? "Loading more comments..." : "More comments below"}</span>
          </PaginationLoadingContainer>
        );
      }

      const cardComment = normalizeCommentForCards(item.comment);
      const isExpanded = expandedCardId === cardComment.id;
      const canUndo = isExpanded && showDeleteOnlyAction && canUndoComment ? canUndoComment(cardComment) : false;
      return (
        <div>
          {cardComment.aiId ? (
            <FeedbackCard
              comment={cardComment}
              isExpanded={isExpanded}
              onExpand={onExpandCard}
              onScrollToAnnotation={onScrollToAnnotation}
              onApply={onApply}
              onDismiss={onDismiss}
              onDelete={onDeleteComment}
              shouldWiggle={wiggleId === cardComment.id}
              installations={installations}
              documentId={documentId}
              commentLocation={commentLocation}
              isMobile={isMobile}
              showDeleteOnlyAction={showDeleteOnlyAction}
              commentFilterForAnalytics={selectedFilter}
              onUndo={onUndoComment}
              canUndo={canUndo}
            />
          ) : (
            <UserCommentCard
              comment={cardComment}
              isExpanded={isExpanded}
              onExpand={onExpandCard}
              onScrollToAnnotation={onScrollToAnnotation}
              onDelete={onDeleteComment}
              documentId={documentId}
              commentLocation={commentLocation}
              isMobile={isMobile}
              shouldWiggle={wiggleId === cardComment.id}
              showDeleteOnlyAction={showDeleteOnlyAction}
              commentFilterForAnalytics={selectedFilter}
              onUndo={onUndoComment}
              canUndo={canUndo}
            />
          )}
        </div>
      );
    },
    [
      canUndoComment,
      children,
      commentLocation,
      documentId,
      expandedCardId,
      installations,
      isMobile,
      isLoadingMoreComments,
      onApply,
      onDeleteComment,
      onDismiss,
      onExpandCard,
      onScrollToAnnotation,
      onUndoComment,
      selectedFilter,
      showDeleteOnlyAction,
      wiggleId,
    ]
  );

  return (
    <SidebarContainer data-testid="comment-sidebar" $width={sidebarWidth}>
      <CommentSidebarResizeHandle
        width={sidebarWidth}
        onResize={setCommentSidebarWidthPreview}
        onResizeEnd={(width) => {
          setCommentSidebarWidth(width);
          setCommentSidebarWidthPreview(null);
        }}
      />
      <SidebarInner style={sidebarStyle}>
        <SidebarHeader
          selectedFilter={selectedFilter}
          commentCounts={commentCounts}
          onNavigateUp={onNavigateUp}
          onNavigateDown={onNavigateDown}
          onDeleteAll={onDeleteAll}
          onFilterChange={onFilterChange ?? (() => undefined)}
          canNavigateUp={canNavigateUp}
          canNavigateDown={canNavigateDown}
        />
        {comments.length === 0 && !children ? (
          <ScrollableCardArea ref={scrollRef}>
            <EmptyStateContainer>
              <div className="flex items-center gap-2 mb-5">
                <SVG
                  src="/assets/comment-icon.svg"
                  width={28}
                  height={28}
                />
                {emptyState.showComposerHintIcons && (
                  <SVG
                    src="/assets/edit2.svg"
                    width={24}
                    height={24}
                    data-testid="comment-empty-edit-icon"
                    className="[&_path]:fill-app-grey-1"
                  />
                )}
              </div>
              <EmptyStateMessage>{emptyState.message}</EmptyStateMessage>
            </EmptyStateContainer>
          </ScrollableCardArea>
        ) : (
          <Virtuoso
            ref={virtuosoRef}
            data={listItems}
            components={virtuosoComponents}
            computeItemKey={computeItemKey}
            itemContent={renderCommentItem}
            initialItemCount={Math.min(listItems.length, 12)}
            endReached={handleEndReached}
            overscan={{ main: 600, reverse: 300 }}
            increaseViewportBy={{ top: 250, bottom: 400 }}
            scrollerRef={(ref) => {
              scrollRef.current = ref instanceof HTMLElement ? ref as HTMLDivElement : null;
            }}
          />
        )}
      </SidebarInner>
    </SidebarContainer>
  );
}

export default CommentSidebar;
